import React, { useEffect, useRef, useCallback } from 'react';
import { Icon } from '../Icons';
import type { ContextMenuItem, Position } from '../../types';
import styles from './ContextMenu.module.css';

interface ContextMenuProps {
  items: ContextMenuItem[];
  position: Position;
  onClose: () => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ items, position, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Adjust position if menu would go off-screen
  const getAdjustedPosition = useCallback((): React.CSSProperties => {
    const menuWidth = 180;
    const menuHeight = items.length * 28;

    let x = position.x;
    let y = position.y;

    // Adjust if would go off right edge
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 5;
    }

    // Adjust if would go off bottom edge
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 5;
    }

    return { left: x, top: y };
  }, [position, items.length]);

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Delay adding listeners to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleItemClick = (item: ContextMenuItem) => {
    if (item.disabled) return;
    if (item.action) {
      item.action();
    }
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className={styles.contextMenu}
      style={getAdjustedPosition()}
      role="menu"
      aria-label="Context menu"
    >
      {items.map((item, index) => (
        item.divider ? (
          <div key={`divider-${index}`} className={styles.divider} role="separator" />
        ) : (
          <button
            key={item.id}
            className={`${styles.menuItem} ${item.disabled ? styles.disabled : ''}`}
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
            role="menuitem"
          >
            {item.icon && (
              <span className={styles.iconWrapper}>
                <Icon name={item.icon} size={16} />
              </span>
            )}
            <span className={styles.label}>{item.label}</span>
          </button>
        )
      ))}
    </div>
  );
};

export default ContextMenu;
